import {AssistantVoiceCapabilities} from '../api/voice/useAssistantVoiceCapabilities';
import {humanizeToolName} from './runOutcomeSummary';
import {sliceSpokenText} from './voiceMessageText';
import type {VoiceActionResult} from './VoiceControlSurfaceContext';

/**
 * Spoken answers about what an assistant can do, shared by the open assistant's
 * describe action and the describe-by-name action available everywhere, so both
 * say the same thing about the same profile.
 */

export const capabilityDescriptionParts = [
    'overview',
    'tools',
    'connectors',
    'knowledge',
    'skills',
    'prompts',
    'guide'
] as const;

export type CapabilityDescriptionPart =
    (typeof capabilityDescriptionParts)[number];

export const capabilityDescriptionParameters = {
    part: {
        type: 'string',
        enum: [...capabilityDescriptionParts],
        description:
            'overview for a general answer; tools, connectors, knowledge, skills, or prompts for one aspect; guide to read its user guide.'
    },
    offset: {
        type: 'integer',
        description:
            'For part guide: character offset to continue reading from, taken from the previous result.'
    }
} as const;

const joinForSpeech = (items: string[], max = 8) => {
    const shown = items.slice(0, max);
    const more = items.length - shown.length;
    const list =
        shown.length <= 1
            ? (shown[0] ?? '')
            : shown.length === 2
              ? `${shown[0]} and ${shown[1]}`
              : `${shown.slice(0, -1).join(', ')}, and ${shown[shown.length - 1]}`;
    return more > 0 ? `${list}, and ${more} more` : list;
};

const speak = (text: string, data?: unknown): VoiceActionResult => ({
    status: 'ready',
    message: text,
    speakText: text,
    data
});

export const describeAssistantCapabilities = (
    info: AssistantVoiceCapabilities,
    part: string,
    offset = 0
): VoiceActionResult => {
    if (part === 'tools') {
        const features = info.features.map(f => f.label);
        const servers = info.mcpServers.map(server =>
            server.toolNames && server.toolNames.length > 0
                ? `${server.displayName} with ${joinForSpeech(
                      server.toolNames.map(humanizeToolName),
                      10
                  )}`
                : server.toolCount
                  ? `${server.displayName} with ${server.toolCount} tools`
                  : server.displayName
        );
        const parts = [
            features.length > 0
                ? `Its features are ${joinForSpeech(features)}.`
                : 'It has no extra features enabled.',
            servers.length > 0
                ? `Its tool servers are ${joinForSpeech(servers, 5)}.`
                : '',
            info.mcpServers.some(server => server.requiresApproval)
                ? 'Some of those tools ask for your approval before they run.'
                : ''
        ].filter(Boolean);
        return speak(parts.join(' '), {
            features: info.features,
            mcpServers: info.mcpServers
        });
    }

    if (part === 'connectors') {
        const names = info.connectors.map(c => c.displayName);
        return speak(
            names.length > 0
                ? `It is connected to ${joinForSpeech(names)}.`
                : 'It has no connected services.',
            {connectors: info.connectors}
        );
    }

    if (part === 'knowledge') {
        const count = info.knowledgeFileCount;
        const canSearch = info.features.some(
            feature => feature.id === 'file_search'
        );
        return speak(
            count > 0
                ? `It has ${count} knowledge ${count === 1 ? 'file' : 'files'}${
                      canSearch ? ' it can search' : ''
                  }.`
                : 'It has no knowledge files.',
            {knowledgeFileCount: count, canSearch}
        );
    }

    if (part === 'skills') {
        const names = info.skills.map(skill => skill.name);
        return speak(
            names.length > 0
                ? `Its skills are ${joinForSpeech(names)}.`
                : 'It has no skills.',
            {skills: info.skills}
        );
    }

    if (part === 'prompts') {
        const prompts = info.starterPrompts.map(p => p.buttonText);
        return speak(
            prompts.length > 0
                ? `Its suggested prompts are: ${prompts
                      .map((text, index) => `${index + 1}. ${text}`)
                      .join('; ')}. Say the number to send one.`
                : 'It has no suggested prompts.',
            {prompts}
        );
    }

    if (part === 'guide') {
        const guide =
            info.userGuideExcerpt?.trim() || info.description?.trim() || '';
        if (!guide) {
            return speak('It has no user guide or description.');
        }
        const slice = sliceSpokenText(guide, offset);
        return {
            status: 'ready',
            message: 'User guide captured.',
            speakText: slice.text,
            data: slice
        };
    }

    const overview = [
        info.spokenSummary,
        info.description ? `It is described as: ${info.description}` : '',
        info.modelLabel ? `It uses the model ${info.modelLabel}.` : ''
    ]
        .filter(Boolean)
        .join(' ');
    const slice = sliceSpokenText(overview);
    return {
        status: 'ready',
        message: slice.text,
        speakText: slice.text,
        data: {
            summary: info.spokenSummary,
            description: info.description,
            modelLabel: info.modelLabel,
            knowledgeFileCount: info.knowledgeFileCount,
            connectors: info.connectors.map(c => c.displayName),
            mcpServers: info.mcpServers.map(s => s.displayName),
            skills: info.skills.map(s => s.name),
            unavailableReason: info.unavailableReason
        }
    };
};
